import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useMe, useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import type { MerchantSettings, SenderDetails } from '@/api/types/settings';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { FormCountryField, FormSelectField, FormTextField } from '@/components/form';
import { HIGHLIGHT_RING, useHighlightSection } from '@/hooks/use-highlight-section';
import { applyApiErrors, toApiProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';

import { SettingsCard, SettingsSection } from '../components/SettingsSection';
import { DeliveryEstimateSection } from '../sections/DeliveryEstimateSection';
import { CashOnDeliverySection } from '../sections/CashOnDeliverySection';
import { FulfillmentSection } from '../sections/FulfillmentSection';
import { ShippingDefaultsSection } from '../sections/ShippingDefaultsSection';
import { senderSettingsSchema, type SenderSettingsFormValues } from '../schemas';

const emptySender: SenderDetails = {
  company_name: null,
  first_name: '',
  last_name: '',
  email: '',
  phone_prefix: '+48',
  phone_number: '',
  street: '',
  house_number: '',
  flat_number: null,
  city: '',
  postal_code: '',
  country_code: 'PL',
  subdivision_code: null,
};

// ISO 3166-2 regions InPost accepts for GB senders; labels resolved via i18n.
const GB_SUBDIVISION_CODES = ['GB-ENG', 'GB-SCT', 'GB-WLS', 'GB-GBN', 'GB-NIR'] as const;

function toFormValues(s: MerchantSettings | undefined): SenderSettingsFormValues {
  const sender = { ...emptySender, ...(s?.sender_details ?? {}) };
  return {
    sender_details: {
      company_name: sender.company_name ?? null,
      first_name: sender.first_name ?? '',
      last_name: sender.last_name ?? '',
      email: sender.email ?? '',
      phone_prefix: sender.phone_prefix ?? '+48',
      phone_number: sender.phone_number ?? '',
      street: sender.street ?? '',
      house_number: sender.house_number ?? '',
      flat_number: sender.flat_number ?? null,
      city: sender.city ?? '',
      postal_code: sender.postal_code ?? '',
      country_code: sender.country_code ?? 'PL',
      subdivision_code: sender.subdivision_code ?? null,
    },
  };
}

type GeneralTabProps = {
  dirtyRef: (dirty: boolean) => void;
};

/**
 * Sender details are the only form-managed block (cross-field validation, so a
 * Save button lives inside its card); shipping defaults and delivery estimate
 * save instantly per control, matching the Checkout tab.
 */
export function GeneralTab({ dirtyRef }: GeneralTabProps) {
  const { t } = useTranslation('settings');
  const tCommon = useTranslation('common').t;
  const meQuery = useMe();
  const update = useUpdateMerchantSettings();

  const settings = meQuery.data?.settings;
  const insuranceCurrency = meQuery.data?.merchant.insurance_currency ?? null;
  const initial = useMemo(() => toFormValues(settings), [settings]);

  const senderHighlight = useHighlightSection<HTMLElement>('sender-details');

  const form = useForm<SenderSettingsFormValues>({
    resolver: zodResolver(senderSettingsSchema),
    values: initial,
    mode: 'onSubmit',
  });

  const senderCountry = form.watch('sender_details.country_code');

  const gbSubdivisionOptions = useMemo(
    () =>
      GB_SUBDIVISION_CODES.map((code) => ({
        value: code,
        label: t(`general.senderDetails.subdivisions.${code}`),
      })),
    [t],
  );

  useEffect(() => {
    dirtyRef(form.formState.isDirty);
  }, [form.formState.isDirty, dirtyRef]);

  const onSubmit = (values: SenderSettingsFormValues) => {
    update.mutate(
      { settings: values },
      {
        onSuccess: () => {
          toast.success(t('saved'));
          form.reset(values);
        },
        onError: (error) => applyApiErrors(form, toApiProblem(error)),
      },
    );
  };

  if (meQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">{tCommon('states.loading')}</p>;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <SettingsSection
            ref={senderHighlight.ref}
            title={t('general.senderDetails.title')}
            description={t('general.senderDetails.description')}
            className={cn(senderHighlight.isHighlighted && HIGHLIGHT_RING)}
          >
            <SettingsCard>
              {/* Field rows grouped per prototype-2.html. */}
              <div className="flex flex-col gap-4 py-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.first_name"
                    label={t('general.senderDetails.firstName')}
                    required
                    autoComplete="given-name"
                  />
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.last_name"
                    label={t('general.senderDetails.lastName')}
                    required
                    autoComplete="family-name"
                  />
                </div>
                <FormTextField<SenderSettingsFormValues>
                  name="sender_details.company_name"
                  label={t('general.senderDetails.companyName')}
                />
                <div className="grid gap-4 md:grid-cols-[1fr_7rem_1fr]">
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.email"
                    type="email"
                    label={t('general.senderDetails.email')}
                    required
                    autoComplete="email"
                  />
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.phone_prefix"
                    label={t('general.senderDetails.phonePrefix')}
                    required
                    placeholder="+48"
                  />
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.phone_number"
                    type="tel"
                    label={t('general.senderDetails.phoneNumber')}
                    required
                    autoComplete="tel-national"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-[1fr_8rem_8rem]">
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.street"
                    label={t('general.senderDetails.street')}
                    required
                    autoComplete="address-line1"
                  />
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.house_number"
                    label={t('general.senderDetails.houseNumber')}
                    required
                  />
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.flat_number"
                    label={t('general.senderDetails.flatNumber')}
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-[9rem_1fr_1fr]">
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.postal_code"
                    label={t('general.senderDetails.postalCode')}
                    required
                    autoComplete="postal-code"
                  />
                  <FormTextField<SenderSettingsFormValues>
                    name="sender_details.city"
                    label={t('general.senderDetails.city')}
                    required
                    autoComplete="address-level2"
                  />
                  <FormCountryField<SenderSettingsFormValues>
                    name="sender_details.country_code"
                    label={t('general.senderDetails.country')}
                    required
                  />
                </div>
                {senderCountry === 'GB' && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormSelectField<SenderSettingsFormValues>
                      name="sender_details.subdivision_code"
                      label={t('general.senderDetails.subdivision')}
                      options={gbSubdivisionOptions}
                      required
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 py-4">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!form.formState.isDirty || update.isPending}
                  onClick={() => form.reset(initial)}
                >
                  {tCommon('actions.discardChanges')}
                </Button>
                <Button type="submit" disabled={!form.formState.isDirty || update.isPending}>
                  {update.isPending ? tCommon('actions.saving') : tCommon('actions.save')}
                </Button>
              </div>
            </SettingsCard>
          </SettingsSection>
        </form>
      </Form>

      <ShippingDefaultsSection
        labelFormat={settings?.default_label_format ?? null}
        insuranceValue={settings?.default_insurance_value ?? null}
        sendingMethod={settings?.default_sending_method ?? null}
        defaultPackageKey={settings?.default_package_key ?? null}
        eoriNumber={settings?.eori_number ?? null}
        insuranceCurrency={insuranceCurrency}
      />

      <FulfillmentSection
        syncEnabled={settings?.fulfillment_sync_enabled ?? true}
        notifyCustomer={settings?.fulfillment_notify_customer ?? true}
      />

      <CashOnDeliverySection markPaidEnabled={settings?.cod_mark_paid_enabled ?? true} />

      <DeliveryEstimateSection
        pickPackDays={settings?.pick_pack_time_days ?? 1}
        transitDays={settings?.transit_time_days ?? 2}
      />
    </div>
  );
}
